import React, { useEffect, useState } from "react"
import CoachService from "../../components/general/coach-preview/CoachService"
import { useNavigate, useSearchParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faGraduationCap, faLocationDot, faLongArrowLeft, faStar } from "@fortawesome/free-solid-svg-icons"
import Header from "../../components/navigation/Header"
import Footer from "../../components/navigation/Footer"
import GetWindowSize from "../../hooks/general/GetWindowSize"
import axios from "axios"
import GetCSFR from "../../hooks/userAuthentication/GetCSFR"
import ReviewSlider from "../../components/general/coach-preview/ReviewSlider"
import SocialMediaIcons from "../../components/general/coach-preview/SocialMediaIcons"
import RetrieveImage from "../../hooks/images/RetrieveImage"
import { UserContext } from "../../hooks/images/RetrieveImageContext"
import ContactCoachForm from "../../components/general/coach-preview/ContactCoachForm"
import LoadingAnimation from "../../components/general/LoadingAnimation"

interface Review {
    id: number;
    title: string;
    description: string;
    rating: string;
    date: string;
}

interface Service {
    id: number;
    type: string;
    title: string;
    description: string;
    duration: string;
    frequency?: string;
    target_audience?: string;
    session_length?: number;
    location?: string;
    deliverable?: string;
    price: number;
} 
  
interface ProfileDetails {
    userId: string;
    fullname: string;
    profile: {
        location: string;
        biography: string;
        primarySport: string;
        picture: string | null;
        reviews: Review[];
        services: Service[];
        rating: number;
        socialMedia: {
            instagram: string,
            facebook: string,
            twitter: string,
            tiktok: string
        }
    };
    preferences: {
        experience_level: string;
        specialization: string[];
    }
}

export default function Coach(){
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [profileDetails, setProfileDetails] = useState<ProfileDetails>({
        userId: '',
        fullname: '',
        profile: {
            location: '',
            biography: '',
            primarySport: '',
            picture: '',
            reviews: [],
            services: [],
            rating: 0,
            socialMedia: {
                instagram: '',
                facebook: '',
                twitter: '',
                tiktok: ''
            }
        },
        preferences: {
            experience_level: '',
            specialization: []
        }
    })
    const [selectedService, setSelectedService] = useState<Service>()
    const windowSize = GetWindowSize()
    const navigate = useNavigate()
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const [queryParameters] = useSearchParams()
    const [userEmail, setUserEmail] = useState<string>("")
    const [loading, setLoading] = useState<boolean>(true)
    // Get Back parameter to determine if user has come from a specialized page that cannot be revisited (e.g order review page)
    const preventNavigation =queryParameters.get('allowed_back')

    // API call to get coach details and user email
    useEffect(()=>{
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/coach?coach_id=${queryParameters.get("coach_id")}`, { 
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        }) 
        .then(res => {
            if (res.status === 200) {
                setProfileDetails(res.data)
            } else {
                console.error("Failed to retrieve coach details")
            }
        })
        .catch(error => {console.error(error)})

        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/get_user_email/`, { 
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        }) 
        .then(res => {
            if (res.status === 200) {
                setUserEmail(res.data.user_email)
            } else {
                console.error("Failed to retrieve user email")
            }
        })
        .catch(error => {console.error(error)})
        .finally(()=>setLoading(false))
    },[])

    // Update document title with Coach Profile name
    useEffect(()=>{
        document.title = `SkillJa - ${profileDetails.fullname}'s Profile`
    },[profileDetails])

    return (
        <div className="flex flex-col">
            { (userEmail) && ( 
                <UserContext.Provider value={{imageName:userEmail, cache:true}}>
                    <Header useCase="protected" /> 
                </UserContext.Provider>
            )}
            {loading ?
                <div className="mt-32 mb-44">
                    <LoadingAnimation />
                </div>
            :
                <div className={`pb-4 px-8 lg:px-32 xl:px-40 lg:mb-32 ${loading ? 'opacity-0':'opacity-100'}`}>
                    <section className="grid grid-cols-3 items-center">
                        <FontAwesomeIcon 
                            icon={faLongArrowLeft}
                            onClick={()=>preventNavigation ? navigate('/home-feed'): navigate(-1)} 
                            className="text-3xl hover:text-main-green-500 cursor-pointer justify-self-start" 
                        />
                        <h1 className="font-kulim text-2xl sm:text-2xl lg:text-3xl text-center text-main-green-900 font-semibold">
                            {profileDetails ? profileDetails.fullname : 'Profile'}
                        </h1>
                        <p className="w-6 ml-auto mr-0"></p>
                    </section>

                    <section className="flex flex-col border-b-4 mt-10 lg:mt-14 border-main-grey-200 pb-4 sm:pb-8">
                        <div className={`flex ${windowSize.width < 1200 && 'flex-wrap'} items-start`}>
                            <div className={`flex flex-wrap shrink md:shrink-0 max-w-full ${windowSize.width < 1200 && 'mx-auto'} `}>
                                {profileDetails.profile.picture && (
                                    <UserContext.Provider value={{imageName:profileDetails.profile.picture!, cache:false}}>
                                        <RetrieveImage id={`${profileDetails.profile.picture!}`} position="one" styling="w-40 h-40 md:w-60 md:h-60 rounded-2xl mr-3 xl:mr-6"/>
                                    </UserContext.Provider> 
                                )}
                                {!profileDetails.profile.picture && (
                                    <img src={require('../../assets/default-avatar.jpg')} className="w-40 h-40 md:w-60 md:h-60 rounded-2xl lg:mr-3 xl:mr-6" />
                                )}
                                <div className={`grid ${windowSize.width < 420 ? 'grid-cols-1 grid-rows-4 gap-1' : 'grid-cols-2 grid-rows-2 gap-4'} min-w-fit ${windowSize.width > 1190 && 'mr-4'} xl:mr-6`}>
                                    {profileDetails.profile.picture ? (
                                        <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}two`, cache:false}}>
                                            <RetrieveImage id={`${profileDetails.profile.picture!}two`} position='two' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`} />
                                        </UserContext.Provider>
                                    )
                                    :
                                        <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                    }
                                    {profileDetails.profile.picture ? (
                                        <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}three`, cache:false}}>
                                            <RetrieveImage id={`${profileDetails.profile.picture!}three`} position='three' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`} />
                                        </UserContext.Provider>
                                    )
                                    :
                                        <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                    }
                                    {profileDetails.profile.picture ? (
                                        <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}four`, cache:false}}>
                                            <RetrieveImage id={`${profileDetails.profile.picture!}four`} position='four' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border object-cover`}/>
                                        </UserContext.Provider>
                                    )
                                    :
                                        <img src={require('../../assets/default-avatar.jpg')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                    }
                                    {profileDetails.profile.picture ? (
                                        <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}video`, cache:false}}>
                                            <RetrieveImage id={`${profileDetails.profile.picture!}video`} position='video' styling={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`} />
                                        </UserContext.Provider>
                                    )
                                    :
                                        <img src={require('../../assets/default-video-avatar.png')} className={`${windowSize.width < 420 ? 'w-9 h-9' : 'w-16 h-16'} md:min-w-28 md:min-h-28 rounded-lg sm:rounded-2xl border`}/>
                                    }
                                </div>
                            </div>
                            <div className={`flex flex-col justify-start items-start w-full break-words max-w-lg xl:max-w-xl ${windowSize.width > 1200 ? 'ml-auto' :'mx-auto mt-9 mb-5'} min-w-0 mb-auto`}>
                                <div className="flex w-full items-start">
                                    <div className="flex flex-col justify-center items-start font-kulim text-main-green-900 md:pr-16">
                                        <h3 className="text-sm sm:text-base mb-1 mx-auto font-medium font-kulim ml-0">
                                            <FontAwesomeIcon icon={faLocationDot} className="text-base md:text-xl w-6 mr-1.5 md:mr-3.5" />
                                            {profileDetails.profile.location? profileDetails.profile.location : 'Location' }
                                        </h3>
                                        <h3 className="text-sm sm:text-base my-6 sm:my-5 xl:my-5 mx-auto font-medium font-kulim ml-0">
                                            <FontAwesomeIcon icon={faGraduationCap} className="text-base md:text-xl w-6 mr-1.5 md:mr-3.5" />
                                            {profileDetails.preferences.experience_level ? `${profileDetails.preferences.experience_level.replace(/[\[\]\"\'']/g,'')} coaching `: 'N/A' }
                                        </h3>
                                        <h3 className="text-sm sm:text-base mt-1 mx-auto font-medium ml-0">
                                            <FontAwesomeIcon icon={faStar} className="text-amber-500 w-6 text-base md:text-xl mr-1.5 md:mr-3.5" />
                                            {Number(profileDetails.profile?.rating).toPrecision(2).toString().includes('0.0') || profileDetails.profile?.rating.toString() === '0' ? 'No Ratings' : Number(profileDetails.profile?.rating).toPrecision(2)} 
                                        </h3>
                                    </div>
                                    {/* <div className="flex flex-col justify-evenly items-end ml-auto text-sm"> */}
                                     <div className="flex flex-col gap-4 items-end ml-auto text-sm">
                                        {profileDetails.profile.picture && 
                                            <UserContext.Provider value={{imageName:`${profileDetails.profile.picture!}club`, cache:false}}>
                                                <RetrieveImage id={`${profileDetails.profile.picture!}club`} position='club' styling="object-cover rounded-xl w-20 h-7" />
                                            </UserContext.Provider>
                                        }
                                        <h3 className="py-1 px-3 text-sm rounded-xl  bg-main-white border border-main-grey-100">
                                            {profileDetails.profile?.primarySport || 'N/A'}
                                        </h3>
                                        <div className="flex">
                                            <SocialMediaIcons 
                                                instagram={profileDetails.profile?.socialMedia.instagram}
                                                facebook={profileDetails.profile?.socialMedia.facebook}
                                                twitter={profileDetails.profile?.socialMedia.twitter}
                                                tiktok={profileDetails.profile?.socialMedia.tiktok}
                                            /> 
                                            <div className="ml-2.5">
                                                <ContactCoachForm csrftoken={csrfToken!} coachId={queryParameters.get("coach_id")!}/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full mt-7 sm:mt-4 md:mt-6 lg:mt-6 mb-auto text-wrap pl-1">
                                    <h3 className="text-sm sm:text-base my-1 mr-auto font-semibold font-kulim">About Me:</h3>
                                    <p className="mt-1 min-h-16 text-sm sm:text-base text-start font-kulim lg:leading-snug">
                                        {profileDetails.profile.biography? profileDetails.profile.biography : 'Bio goes here...' }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="flex justify-center items-center flex-col lg:flex-row lg:items-start my-8 mx-auto lg:pt-6">
                        <div className="flex flex-col justify-center w-full lg:w-5/12 lg:mr-auto">
                            <h2 className="text-lg sm:text-xl font-medium mx-auto mb-6 text-center">
                                Sessions and Packages
                            </h2>
                            {profileDetails.profile?.services?.length > 0 ?
                                <>
                                    {profileDetails.profile.services.map((currService, index) => (
                                    <div
                                        className="flex rounded-2xl py-2 px-5 mb-4 bg-main-white border border-main-grey-100 cursor-pointer hover:border-main-green-500 hover:shadow-md"
                                        onClick={()=>{
                                            setIsModalOpen(true)
                                            setSelectedService(currService)
                                        }}
                                        key={index}
                                    >
                                        <div className="flex flex-col justify-start items-start font-kulim">
                                            <h3 className="my-1 mr-auto">
                                                {currService.title}
                                            </h3>
                                            <p className="text-sm my-1 mr-auto">
                                                See Price and Details
                                            </p>
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} className="text-main-grey-300 ml-auto my-auto" />
                                    </div>
                                    ))}
                                </>
                            :
                                <p className="mx-auto font-kulim">No sessions are currently available</p>
                            }
                        </div>
                        <div className="flex flex-col justify-center mt-12 lg:mt-0 w-full lg:w-5/12 lg:ml-auto">
                            <h2 className="text-lg sm:text-xl font-medium mx-auto mb-6 text-center">
                                Reviews and Testimonials
                            </h2>
                            {profileDetails.profile?.reviews?.length > 0 ?
                                <ReviewSlider data={profileDetails.profile.reviews}/>   
                            :
                                <p className="mx-auto mt-4 font-kulim">No reviews available</p>    
                            }
                        </div>
                    </section>
                </div>
            }
            {isModalOpen && <CoachService data={selectedService!} exitView={setIsModalOpen}/> }
            <Footer />
        </div>
    )
}